import { useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/**
 * Modal dialog for entering annotation text when placing a new annotation.
 * Props:
 *   open      {boolean}          - Whether the dialog is visible
 *   onSubmit  {(text: string) => void} - Called with the entered text
 *   onCancel  {() => void}       - Called when the user cancels
 *   t         {Function}         - i18next translation function
 */
export function AnnotationInputDialog({ open, onSubmit, onCancel, t }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.value = '';
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    const text = inputRef.current?.value.trim();
    if (text) {
      onSubmit(text);
    } else {
      onCancel();
    }
  }, [onSubmit, onCancel]);

  if (!open) return null;

  return (
    <Box sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1300,
      bgcolor: 'rgba(20,20,30,0.6)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      borderRadius: '12px',
      p: 2.5,
      minWidth: 300,
    }}>
      <Typography sx={{ color: '#fff', fontSize: '0.85rem', mb: 1.5 }}>
        {t('EnterAnnotationText') || 'Enter annotation text:'}
      </Typography>
      <input
        ref={inputRef}
        type="text"
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '0.85rem',
          color: '#fff',
          backgroundColor: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '8px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
        <Box
          component="button"
          onClick={onCancel}
          sx={{
            px: 2, py: 0.75,
            fontSize: '0.8rem',
            color: '#fff',
            bgcolor: 'transparent',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          {t('Cancel') || 'Cancel'}
        </Box>
        <Box
          component="button"
          onClick={handleSubmit}
          sx={{
            px: 2, py: 0.75,
            fontSize: '0.8rem',
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '6px',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
          }}
        >
          OK
        </Box>
      </Box>
    </Box>
  );
}

export default AnnotationInputDialog;
